import { Injectable } from '@angular/core';
import { HttpRestService } from '@core/services/http-rest.service';
import { AutomationAPI } from '@core/typings/api/automation.typing';
import { DetailUnion, WorkflowLevelAdvancementAPI, WorkflowLevelAutomationDetail } from '@core/typings/api/workflow-level-advancement.typing';
import { WorkflowLevelAutomationAPI } from '@core/typings/api/workflow-level-automation.typing';
import { APIResult, APIResultData, PaginationOptions } from '@yourcause/common';

@Injectable({ providedIn: 'root' })
export class WorkflowLevelAutomationResources {

  constructor (
    private httpRestService: HttpRestService
  ) { }

  protected apiBase = '/api/manager/WorkflowLevelAutomation';
  protected searchHistoryRuleSetParam = 'workflowLevelAutomationRuleSetId';

  saveLevel (
    level: WorkflowLevelAutomationAPI.SaveWorkflowLevelAutomationRuleSet|WorkflowLevelAdvancementAPI.SaveWorkflowLevelRoutingAutomationRuleSet
  ) {
    return this.httpRestService.post<WorkflowLevelAutomationDetail>(this.apiBase, level);
  }

  getFormsByWorkflowId (workflowId: number) {
    return this.httpRestService.get<WorkflowLevelAutomationAPI.WorkflowFormDisplay[]>(`${this.apiBase}/Forms?workflowId=${workflowId}`);
  }

  async searchHistory (
    paginationOptions: PaginationOptions<WorkflowLevelAutomationAPI.WorkflowLevelAutomationApplication|WorkflowLevelAdvancementAPI.WorkflowLevelAutomationApplication>,
    id?: number
  ) {
    const data = await this.httpRestService.post<APIResult<WorkflowLevelAutomationAPI.WorkflowLevelAutomationApplication|WorkflowLevelAdvancementAPI.WorkflowLevelAutomationApplication>>(
      this.apiBase + '/Applications', {
        [this.searchHistoryRuleSetParam]: id,
        paginationOptions
      }
    );

    return ({
      success: true,
      data
    });
  }

  searchRuleSets<C extends boolean> (
    paginationOptions: PaginationOptions<C extends true ?
      WorkflowLevelAdvancementAPI.WorkflowLevelRoutingAutomationRuleSetDetailModel :
      WorkflowLevelAutomationAPI.WorkflowLevelAutomationRuleSetModel>,
    _isAdvancement: C
  ): Promise<APIResultData<C extends true ?
      WorkflowLevelAdvancementAPI.WorkflowLevelRoutingAutomationRuleSetDetailModel :
      WorkflowLevelAutomationAPI.WorkflowLevelAutomationRuleSetModel>> {
    const endpoint = this.apiBase + '/Paginated';
   
    return this.httpRestService.post(endpoint, {
      paginationOptions
    });
  }

  getRuleSet (ruleSetId: number) {
    return this.httpRestService.get<DetailUnion>(this.apiBase + '/' + ruleSetId);
  }

  changeRuleSetStatus (ruleSetId: number, status: AutomationAPI.AutomationRuleSetStatus) {
    return this.httpRestService.post<null>(this.apiBase + `/${ruleSetId}/Status`, {
      status
    });
  }

  changeRuleSetStatusBulk (
    ids: number[],
    status: AutomationAPI.AutomationRuleSetStatus,
    isAdvancement: boolean
  ) {
    const endpoint = this.apiBase + '/' +
      (isAdvancement ? 'BulkUpdateWflRoutingAutomationRuleSetStatus' :'BulkUpdateWflAutomationRuleSetStatus');

    return this.httpRestService.post(endpoint, {
      ids,
      status
    });
  }

  deleteRuleSet (ruleSetId: number) {
    return this.httpRestService.delete(this.apiBase + `/${ruleSetId}`);
  }

  sequence (
    workflowId: number,
    formId: number,
    ruleSets: {
      workflowLevelAutomationRuleSetId?: number;
      workflowLevelRoutingAutomationRuleSetId?: number;
      sequence: number;
    }[],
    workflowLevelId?: number
  ) {
    return this.httpRestService.post(this.apiBase + '/Sequence', {
      workflowId,
      formId,
      ruleSets,
      workflowLevelId
    });
  }

  rerouteApplication (
    workflowLevelId: number,
    applicationId: number,
    comments: string
  ) {
    return this.httpRestService.post('/api/manager/WorkflowLevelAutomation/RouteApplication', {
      workflowLevelId,
      applicationId,
      comments
    });
  }

  getSingleResponseRefFields (
    workflowId: number,
    defaultFormId: number
  ): Promise<number[]> {
    const endpoint = `api/manager/WorkflowLevelAutomation/SingleResponseReferenceFields/${workflowId}/${defaultFormId}`;

    return this.httpRestService.get(endpoint);
  }

}

